import { ReactElement, Ref, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import { GestureResponderEvent, LayoutChangeEvent, StyleSheet, View, ViewProps } from 'react-native'
import { fromEvent } from 'rxjs'
import { filter } from 'rxjs/operators'

import { useKeyboardShortcut } from '../../helper'
import { ZoomValue, ZoomView } from '../zoom'

import Positionable, { Placement } from './positionable'

const styles = StyleSheet.create({
  container: {},
  adding: {
    // @ts-expect-error Cursor only relevant for web
    cursor: 'copy',
  },
})

const SNAP_TO = 5

interface Size {
  width: number
  height: number
}

export interface Item {
  key: string
  placement?: Placement
  selected?: boolean
}

type ItemInternal = Item & {
  ref?: Ref<any>
}

export interface ItemInfo {
  item: Item
  index: number
}

export interface Props extends ViewProps {
  items?: Item[]
  renderItem?: (info: ItemInfo) => ReactElement
  onChange?: (value: ItemInfo[]) => void
  onDuplicate?: (value: ItemInfo[]) => void
  onRemove?: (value: ItemInfo) => void
  zoom?: Partial<ZoomValue>
  onZoom?: (value: ZoomValue) => void
  background?: ReactElement
  snapTo?: number
  test_ref?: Ref<any>
}

function mapItemToInfo(item: Item, index: number): ItemInfo {
  return {
    item: { ...item },
    index,
  }
}

const Editable = (props: Props) => {
  const {
    items = [],
    renderItem,
    onChange,
    onDuplicate,
    onRemove,
    zoom,
    onZoom,
    background,
    snapTo = SNAP_TO,
    test_ref,
    style,
    ...otherProps
  } = props
  const [clipboard, setClipboard] = useState<ItemInternal[]>([])
  const [contentSize, setContentSize] = useState<Size>({ width: 0, height: 0 })
  const [itemSizes, setItemSizes] = useState<Record<number, Size>>({})
  const [scale, setScale] = useState(zoom?.scale ?? 1)
  const container = useRef(null)

  function itemIsPlaced(item?: Item) {
    const placement = item?.placement
    if (!placement) return false
    const { x, y, rotation } = placement
    return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(rotation)
  }

  const unplaced = items.filter((item: Item) => !itemIsPlaced(item))
  useLayoutEffect(() => {
    const subscription = fromEvent(container.current as any, 'mousedown')
      .pipe(filter(() => unplaced.length > 0))
      .subscribe((event) => drop(event as GestureResponderEvent))
    return () => subscription.unsubscribe()
  }, [unplaced.length])

  const hasSelectedItems = items.filter((item) => item.selected).length > 0
  useLayoutEffect(() => {
    const subscription = fromEvent(container.current as any, 'mousedown')
      .pipe(filter(() => hasSelectedItems && unplaced.length === 0))
      .subscribe(deselectAll)
    return () => subscription.unsubscribe()
  }, [hasSelectedItems, unplaced])

  function copy() {
    setClipboard([
      ...items
        .filter((item) => item.selected && item.placement)
        .map((item) => ({
          key: item.key,
          placement: { ...item.placement } as Placement,
          selected: item.selected,
        })),
    ])
  }

  function deselectAll() {
    const newItems = items.filter((item) => item.selected).map((item) => ({ ...item, selected: false }))
    onChange?.(newItems.map(mapItemToInfo))
  }

  function paste() {
    function hasDuplicateItems(items: Item[], item: Item) {
      return !!items.find(
        (foundItem) =>
          Math.round(foundItem.placement?.x || 0) === Math.round(item.placement?.x || 0) &&
          Math.round(foundItem.placement?.y || 0) === Math.round(item.placement?.y || 0) &&
          Math.round(foundItem.placement?.rotation || 0) === Math.round(item.placement?.rotation || 0),
      )
    }
    const newItems: Item[] = []
    clipboard.forEach((item, index) => {
      const duplicate = { ...item }
      let placeSouthEast = true
      while (hasDuplicateItems([...items, ...newItems], duplicate)) {
        if (duplicate.placement!.x + itemSizes[index]?.width + 4 * snapTo < contentSize.width && placeSouthEast) {
          duplicate.placement!.x += 4 * snapTo
        } else {
          duplicate.placement!.x -= 4 * snapTo
          placeSouthEast = false
        }
        if (duplicate.placement!.y + itemSizes[index]?.height + 4 * snapTo < contentSize.height && placeSouthEast) {
          duplicate.placement!.y += 4 * snapTo
        } else {
          duplicate.placement!.y -= 4 * snapTo
          placeSouthEast = false
        }
      }
      newItems.push(duplicate)
    })
    onDuplicate?.(newItems.map(mapItemToInfo))
  }

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
  useKeyboardShortcut([isMac ? 'meta' : 'ctrl', 'c'], copy)
  useKeyboardShortcut([isMac ? 'meta' : 'ctrl', 'v'], paste)

  // useKeyboardShortcut(['Escape'], cancel)

  useImperativeHandle(test_ref, () => ({
    layout: (event: LayoutChangeEvent) => {
      layout(event)
    },
  }))

  function drop(event: GestureResponderEvent) {
    // @ts-expect-error Correct coords is in undocumented offset props?
    const { offsetX, offsetY } = event.nativeEvent
    const x = Math.round(offsetX / snapTo) * snapTo
    const y = Math.round(offsetY / snapTo) * snapTo
    const placement: Placement = { x, y, rotation: 0 }
    const index = items.findIndex((item) => !itemIsPlaced(item))
    const { ref: _ref, ...item } = items[index] as ItemInternal
    item.placement = placement
    item.selected = true
    onChange?.([
      mapItemToInfo(item, index),
      ...items
        .filter((data) => data.selected && data.key !== item.key)
        .map((data) => ({ ...data, selected: false }))
        .map(mapItemToInfo),
    ])
  }

  function select(index: number, event: MouseEvent) {
    event.stopPropagation()
    event.preventDefault()
    const { shiftKey } = event
    const oldInfo = items.map((data, index) => {
      const { ref: _ref, ...item } = data as ItemInternal
      return mapItemToInfo(item, index)
    })
    const newInfo: ItemInfo[] = shiftKey ? oldInfo.filter((info) => info.item.selected) : []
    if (!shiftKey) {
      const deselected = oldInfo
        .filter((info, infoIndex) => info.item.selected && index !== infoIndex)
        .map((info) => ({ item: { ...info.item, selected: false }, index: info.index }))
      deselected.forEach((info) => newInfo.push(info))
    }
    newInfo.push({
      index,
      item: { ...oldInfo[index].item, selected: shiftKey ? !oldInfo[index].item.selected : true },
    })
    onChange?.(newInfo)
  }

  function update(index: number, placement: Placement) {
    const { ref: _ref, ...item } = { ...(items as ItemInternal[])[index] }
    const isMoved =
      item.placement?.x !== placement.x ||
      item.placement?.y !== placement.y ||
      item.placement?.rotation !== placement.rotation

    if (isMoved) item.selected = true
    item.placement = placement
    onChange?.([mapItemToInfo(item, index)])
  }

  function remove(index: number) {
    const { ref: _ref, ...item } = { ...(items as ItemInternal[])[index] }
    onRemove?.(mapItemToInfo(item, index))
  }

  function layout(event: LayoutChangeEvent) {
    setContentSize({ width: event.nativeEvent.layout.width, height: event.nativeEvent.layout.height })
  }

  function itemLayout(event: LayoutChangeEvent, index: number) {
    setItemSizes((oldItemSizes) => ({
      ...oldItemSizes,
      [index]: { width: event.nativeEvent.layout.width, height: event.nativeEvent.layout.height },
    }))
  }

  function updateZoom(value: ZoomValue) {
    setScale(value.scale)
    onZoom?.(value)
  }
  return (
    <ZoomView
      style={[styles.container, unplaced.length > 0 && styles.adding, style]}
      initialZoom={zoom}
      onZoom={updateZoom}
      {...otherProps}
    >
      <View ref={container} onLayout={layout} testID="Editable.Content">
        {background && <View pointerEvents="none">{background}</View>}
        {items
          .filter((item) => itemIsPlaced(item))
          .map((item, index) => (
            <Positionable
              test_ref={(item as ItemInternal).ref}
              key={item.key}
              placement={item.placement}
              isSelected={item.selected}
              onSelect={(event) => select(index, event)}
              onUpdate={(position) => update(index, position)}
              onLayout={(event) => itemLayout(event, index)}
              onRemove={onRemove ? () => remove(index) : undefined}
              scale={scale}
              snapTo={snapTo}
              disabled={unplaced.length > 0}
            >
              <View id={item.key}>{renderItem?.({ item, index })}</View>
            </Positionable>
          ))}
      </View>
    </ZoomView>
  )
}

export default Editable
